import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import useSmartState from '../../../../common/hooks/useSmartState';
import { SmartContext } from '../../../../library/Core/SmartContext';
import InquiryReportService from '../InquiryReportService';

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const resolveDateRange = (value: string) => {
    if (!value) return { fromDate: '', toDate: '' };

    const today = new Date();
    const toDate = formatDate(today);
    const from = new Date(today);

    switch (value) {
        case 'TODAY':
            return { fromDate: toDate, toDate };
        case 'LAST_7_DAYS':
            from.setDate(today.getDate() - 6);
            return { fromDate: formatDate(from), toDate };
        case 'LAST_30_DAYS':
            from.setDate(today.getDate() - 29);
            return { fromDate: formatDate(from), toDate };
        case 'LAST_90_DAYS':
            from.setDate(today.getDate() - 89);
            return { fromDate: formatDate(from), toDate };
        default:
            return { fromDate: '', toDate: '' };
    }
};

const FiltersBar = () => {
    const { state } = useContext(SmartContext);
    const { setStateValue } = useSmartState();
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [searchText, setSearchText] = useState(state.data?.searchCriteria?.search || '');
    const requestSequenceRef = useRef(0);

    const searchCriteria = state.data?.searchCriteria || InquiryReportService.getDefaultSearchCriteria();
    const statusOptions = state.data?.filterDomains?.status || [];

    useEffect(() => {
        setSearchText(searchCriteria.search || '');
    }, [searchCriteria.search]);

    useEffect(() => {
        const nextSearch = searchText.trim();
        const timer = setTimeout(() => {
            if (nextSearch !== (searchCriteria.search || '')) {
                setStateValue('searchCriteria', {
                    ...searchCriteria,
                    search: nextSearch,
                    page: 1,
                } as any);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [searchText, searchCriteria.search]);

    const filterSignature = useMemo(() => JSON.stringify({
        search: searchCriteria.search,
        status: searchCriteria.status,
        outcome: searchCriteria.outcome,
        fromDate: searchCriteria.fromDate,
        toDate: searchCriteria.toDate,
        sort: searchCriteria.sort,
        page: searchCriteria.page,
        limit: searchCriteria.limit,
    }), [searchCriteria]);

    useEffect(() => {
        if (isInitialLoad) {
            setIsInitialLoad(false);
            return;
        }

        const requestId = requestSequenceRef.current + 1;
        requestSequenceRef.current = requestId;

        Promise.all([
            InquiryReportService.search(searchCriteria),
            InquiryReportService.getStats(searchCriteria),
        ]).then(([response, stats]) => {
            if (requestSequenceRef.current !== requestId) {
                return;
            }

            const payload = response?.data ?? {};
            setStateValue('inquiryList', payload?.data ?? []);
            setStateValue('pagination', {
                total: payload?.total ?? 0,
                page: searchCriteria.page,
                limit: searchCriteria.limit,
            } as any);
            setStateValue('stats', stats as any);
        }).catch((error) => {
            if (requestSequenceRef.current !== requestId) {
                return;
            }
            console.error('Error loading inquiry results:', error);
            setStateValue('inquiryList', [] as any);
            setStateValue('pagination', {
                total: 0,
                page: searchCriteria.page,
                limit: searchCriteria.limit,
            } as any);
        });
    }, [filterSignature, isInitialLoad]);

    const updateCriteria = (updates: Record<string, any>) => {
        setStateValue('searchCriteria', {
            ...searchCriteria,
            ...updates,
            page: 1,
        } as any);
    };

    const handleDateRangeChange = (value: string) => {
        const { fromDate, toDate } = resolveDateRange(value);
        updateCriteria({ dateRange: value, fromDate, toDate });
    };

    const handleClear = () => {
        const defaultCriteria = InquiryReportService.getDefaultSearchCriteria();
        setSearchText('');
        setStateValue('searchCriteria', defaultCriteria as any);
    };

    return (
        <div className="search_criteria mb-4">
            <div className="filter_body p-4 bg-gray-F2F4F7 rounded border-radius-6">
                <div className="row g-3 align-items-end">
                    <div className="col-lg-4 col-md-6">
                        <label className="form-label font-14 font-500">Search</label>
                        <input
                            className="form-control"
                            placeholder="Search by name, email, or subject"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                        />
                    </div>

                    <div className="col-lg-2 col-md-6">
                        <label className="form-label font-14 font-500">Status</label>
                        <select
                            className="form-select"
                            value={searchCriteria.status || ''}
                            onChange={(event) => updateCriteria({ status: event.target.value })}>
                            <option value="">All Statuses</option>
                            {statusOptions.map((option: any) => (
                                <option key={option.code} value={option.code}>{option.value}</option>
                            ))}
                        </select>
                    </div>

                    <div className="col-lg-3 col-md-6">
                        <label className="form-label font-14 font-500">Date Filter</label>
                        <select
                            className="form-select"
                            value={searchCriteria.dateRange || ''}
                            onChange={(event) => handleDateRangeChange(event.target.value)}>
                            <option value="">All Dates</option>
                            <option value="TODAY">Today</option>
                            <option value="LAST_7_DAYS">Last 7 Days</option>
                            <option value="LAST_30_DAYS">Last 30 Days</option>
                            <option value="LAST_90_DAYS">Last 90 Days</option>
                        </select>
                    </div>

                    <div className="col-lg-2 col-md-6">
                        <label className="form-label font-14 font-500">Sort</label>
                        <select
                            className="form-select"
                            value={searchCriteria.sort || 'newest'}
                            onChange={(event) => updateCriteria({ sort: event.target.value })}>
                            <option value="newest">Newest</option>
                            <option value="oldest">Oldest</option>
                        </select>
                    </div>

                    <div className="col-lg-1 col-md-6">
                        <button type="button" className="btn btn-green btn-sm w-100 height-40" onClick={handleClear}>
                            Clear
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FiltersBar;